import { useEffect, useMemo, useState } from "react";
import { ioRTStore } from "services/rt.socket";
import { AssetType, OperationType } from "static/types";
import { useStore } from "zustand";
import { ExchangeStatus } from "./useExchangeTimeFrame";
import { marketStore } from "entities/market";

interface IProps {
  exchange?: string;
  exchangeStatus?: ExchangeStatus;
  symbol?: string;
  assetType: AssetType;
  ask: number | null | undefined;
  bid: number | null | undefined;
  ask_yield?: number | null;
  bid_yield?: number | null;
  price: number | null | undefined;
  change: number | null | undefined;
  useAssetDepth?: boolean;
  // Option-specific fields for OPRA subscription
  parentSymbol?: string;
  strike?: number | null;
  expiration?: string | null;
  optionType?: number | null;
}
interface IPrice {
  mid: number;
  ask: number;
  bid: number;
  ask_yield: number | null;
  bid_yield: number | null;
  last: number;
  change: number | null;
  changePercent: number | null;
}

const estimateChangePercent = (change: number, price: number) => {
  return +((100 / (price - change)) * change).toFixed(2);
};

const buildOpraTicker = (
  parentSymbol: string,
  expiration: string,
  optionType: number,
  strike: number
): string => {
  const d = new Date(expiration);
  const yy = String(d.getFullYear()).slice(-2);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const cp = optionType === 1 ? "C" : "P";
  const strikeStr = Math.round(strike * 1000).toString().padStart(8, "0");
  return `OPRA~${parentSymbol}  ${yy}${mm}${dd}${cp}${strikeStr}`;
};

const usePrice = (props: IProps): IPrice => {
  const [bid, setBid] = useState<number>(0);
  const [ask, setAsk] = useState<number>(0);
  const [ask_yield, setAskYield] = useState<number | null>(null);
  const [bid_yield, setBidYield] = useState<number | null>(null);
  const [mid, setMid] = useState<number>(0);
  const [last, setLast] = useState<number>(0);
  const [change, setChange] = useState<number | null>(null);
  const [changePercent, setChangePercent] = useState<number | null>(null);

  const { connected, list, subscribe, unsubscribe } = useStore(ioRTStore);
  const { assetDepth } = useStore(marketStore);
  const exchangeCode = props.exchange || "";
  const symbol = props.symbol || "";
  const isExchangeActive = props.exchangeStatus
    ? [
        ExchangeStatus.Open,
        ExchangeStatus.PreMarket,
        ExchangeStatus.PostMarket,
      ].includes(props.exchangeStatus)
    : false;
  const isOpen = props.exchangeStatus === ExchangeStatus.Open;
  const assetType = props.assetType ? +props.assetType : AssetType.Stock;

  const isOption = assetType === AssetType.Option;
  const opraTicker =
    isOption &&
    props.parentSymbol &&
    props.expiration &&
    props.optionType != null &&
    props.strike != null
      ? buildOpraTicker(props.parentSymbol, props.expiration, props.optionType, props.strike)
      : null;

  const ticker = opraTicker ?? `${exchangeCode}~${symbol}`;
  const rtPrice = list[ticker];

  useEffect(() => {
    if (!connected) return;

    if (opraTicker) {
      subscribe(opraTicker);
      return () => {
        unsubscribe(opraTicker);
      };
    }

    if (!exchangeCode || !symbol || !isExchangeActive) return;

    subscribe(ticker);
    return () => {
      unsubscribe(ticker);
    };
  }, [symbol, isExchangeActive, assetType, exchangeCode, connected, opraTicker]);

  const bestDepthBid = useMemo(() => {
    const bids = assetDepth.filter((d) => d.operation === OperationType.Sell);
    if (bids.length === 0) return null;
    let bestBid = null;
    for (const bid of bids) {
      if (!bestBid || bid.price > bestBid.price) {
        bestBid = bid;
      }
    }
    return bestBid;
  }, [assetDepth]);
  const bestDepthAsk = useMemo(() => {
    const asks = assetDepth.filter((d) => d.operation === OperationType.Buy);
    if (asks.length === 0) return null;
    let bestAsk = null;
    for (const ask of asks) {
      if (!bestAsk || ask.price < bestAsk.price) {
        bestAsk = ask;
      }
    }
    return bestAsk;
  }, [assetDepth]);

  useEffect(() => {
    let _bid: number = props.bid ? +props.bid.toFixed(6) : 0,
      _ask: number = props.ask ? +props.ask.toFixed(6) : 0,
      _ask_yield: number | null = props.ask_yield
        ? +props.ask_yield.toFixed(2)
        : null,
      _bid_yield: number | null = props.bid_yield
        ? +props.bid_yield.toFixed(2)
        : null,
      _mid: number = 0,
      _last: number = props.price ? +props.price.toFixed(6) : 0,
      _change: number | null = null,
      _change_percent: number | null = null,
      _delta_price: number | null = null;

    const isStock = [AssetType.Stock, AssetType.ETF, AssetType.Fond].includes(
      assetType
    );

    if (isStock) {
      _bid = 0;
      _ask = 0;
    }

    if (props.useAssetDepth) {
      if (bestDepthBid) {
        _bid = bestDepthBid.price;
        if (bestDepthBid.yield) {
          _bid_yield = +bestDepthBid.yield.toFixed(2);
        }
      }
      if (bestDepthAsk) {
        _ask = bestDepthAsk.price;
        if (bestDepthAsk.yield) {
          _ask_yield = +bestDepthAsk.yield.toFixed(2);
        }
      }
    }

    if (_ask && _bid && !isStock) {
      const midPrice = (_ask + _bid) / 2;
      const fix = assetType === AssetType.Currency ? 4 : midPrice < 2 ? 4 : 2;
      _mid = +midPrice.toFixed(fix);
    }

    if (props.change) {
      const comparePrice = _last || _mid;
      if (comparePrice) {
        _delta_price = comparePrice;
        _change = +props.change.toFixed(5);
        _change_percent = estimateChangePercent(_change, comparePrice);
      }
    }

    if (rtPrice) {
      const lastPrice = rtPrice.last_price;
      // const midPrice = (rtPrice.ask + rtPrice.bid) / 2;
      if (rtPrice.bid && rtPrice.ask) {
        _bid = +rtPrice.bid.toFixed(4);
        _ask = +rtPrice.ask.toFixed(4);
      }

      if (isOpen && lastPrice) {
        _mid = +lastPrice.toFixed(lastPrice < 2 ? 4 : 2);
        _last = +lastPrice.toFixed(lastPrice < 2 ? 4 : 2);

        if (!!_delta_price && _change) {
          const fix = _delta_price < 1 ? 4 : 2;
          _change = _change + (_last - _delta_price);
          _change_percent = estimateChangePercent(_change, _delta_price);
          _change = +_change.toFixed(fix);
        }
      }
    }

    setBid(_bid);
    setAsk(_ask);
    setAskYield(_ask_yield);
    setBidYield(_bid_yield);
    setMid(_mid);
    setLast(_last);
    setChange(_change);
    setChangePercent(_change_percent);
  }, [
    props.ask,
    props.bid,
    props.ask_yield,
    props.bid_yield,
    props.price,
    props.change,
    assetType,
    rtPrice,
    isOpen,
    props.useAssetDepth,
    bestDepthAsk,
    bestDepthBid,
  ]);

  return {
    ask,
    bid,
    ask_yield,
    bid_yield,
    mid,
    last,
    change,
    changePercent,
  };
};

export default usePrice;
